import React from 'react';
import { useHistory, useRouteMatch } from 'react-router-dom';
import { useFlags } from 'launchdarkly-react-client-sdk';

import { FullPageLoader } from 'shared/components/loading';
import { Preview } from '../components/preview';
import { MenuCustomizationLayout } from '../components/menu-customization-layout';
import { useGetMenuCustomization } from '../data-access';
import { useImageBannerForm } from './use-image-banner-form';
import { AddImageBanner } from './add-image-banner';
import { ImageBannerLimit } from './image-banner-limit';
import { ImageBannerList as ImageBannerListItems } from './image-banner-list/image-banner-list';

export function ImageBannerList(): JSX.Element | null {
  const { data, loading } = useGetMenuCustomization();
  const match = useRouteMatch<{ id: string }>('/dispensaries/:id');
  const baseUrl = match?.url;
  const flags = useFlags();
  const showBannerSegmentation = flags['growth.ecomm.banner-segmentation.rollout'] ?? false;

  if (loading) {
    return <FullPageLoader />;
  }

  if (!data) {
    return null;
  }

  return <ImageBannerListContent data={data} baseUrl={baseUrl} showBannerSegmentation={showBannerSegmentation} />;
}

type ImageBannerListContentProps = {
  data: NonNullable<ReturnType<typeof useGetMenuCustomization>['data']>;
  baseUrl: string | undefined;
  showBannerSegmentation: boolean;
};

function ImageBannerListContent({ data, baseUrl, showBannerSegmentation }: ImageBannerListContentProps): JSX.Element {
  const history = useHistory();

  const {
    isDirty,
    imageBanners,
    isImageBannerLimitReached,
    handleRemoveBanner,
    onDragEnd,
    previewData,
    handlePublish,
    handleReset,
  } = useImageBannerForm({ data });

  function handleAddBanner(): void {
    history.push(`${baseUrl ?? ''}/customize/image-banner/add`);
  }

  function handleEditBanner(bannerId: string): void {
    history.push(`${baseUrl ?? ''}/customize/image-banner/edit/${bannerId}`);
  }

  const addComponent = <AddImageBanner disabled={isImageBannerLimitReached} addImageModal={handleAddBanner} />;

  return (
    <MenuCustomizationLayout
      headerText='Image Banner'
      subHeaderText='Display an image carousel above your menu.'
      isDirty={isDirty}
      handlePublish={() => void handlePublish()}
      handleReset={handleReset}
      previewComponent={showBannerSegmentation ? undefined : <Preview.HomePage data={previewData} />}
      actionComponent={showBannerSegmentation ? addComponent : undefined}
    >
      {!showBannerSegmentation && !isImageBannerLimitReached && addComponent}
      {isImageBannerLimitReached && <ImageBannerLimit />}

      <ImageBannerListItems
        imageBanners={imageBanners}
        removeBanner={handleRemoveBanner}
        editBanner={handleEditBanner}
        onDragEnd={onDragEnd}
      />
    </MenuCustomizationLayout>
  );
}
